import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Resolve,
  RouterStateSnapshot,
} from '@angular/router';
import { DatabaseCacheService } from '../../data/cache/database-cache.service';

export interface UserContext {
  sectionId: string | null;
  userId: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class UserContextResolver implements Resolve<UserContext> {
  constructor(private readonly databaseCache: DatabaseCacheService) {}

  async resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<UserContext> {
    const sectionId = this.getParam(route, 'sectionId');
    const userId = this.getParam(route, 'userId');

    // Skip waiting for users list on unauthorized/error pages
    const isErrorPage = this.isErrorRoute(state.url);

    try {
      // Check if section is already being loaded/selected by component (programmatic navigation)
      // This prevents duplicate setSelectedSectionById calls during section switching
      // BUT we must ensure the cache is "ready enough" - not just that sectionId matches
      const currentSection = this.databaseCache.getSelectedSection();
      const sectionDocuments = this.databaseCache.getSectionDocuments();
      const usersList = this.databaseCache.getUsersList();
      const contractEnabled = this.databaseCache.getSelectedSectionContractEnabled();

      // Only skip if:
      // 1. Current section matches target section AND
      // 2. sectionDocuments is loaded (not a cold start) AND
      // 3. Either: contract is CLOSE (no users expected) OR usersList is not null (loading/loaded)
      const isSectionMatch = currentSection?.sectionId === sectionId;
      const isCacheReady = sectionDocuments !== null &&
        (!contractEnabled || usersList !== null);
      const canSkipInitialization = isSectionMatch && isCacheReady;

      // Only wait for initial data if we can't skip
      if (!canSkipInitialization) {
        await this.waitForInitialData(isErrorPage);
      }

      if (sectionId && !canSkipInitialization) {
        await this.databaseCache.setSelectedSectionById(sectionId);
      }

      if (userId) {
        await this.databaseCache.setSelectedUserId(userId);
      }
    } catch (error) {
      console.error('[UserContextResolver] Failed to resolve context', error);
    }

    return { sectionId, userId };
  }

  private isErrorRoute(url: string): boolean {
    // Remove query params, fragments, and trailing slashes for robust matching
    const urlWithoutQuery = url.split('?')[0].split('#')[0].replace(/\/+$/, '');
    return urlWithoutQuery.endsWith('/unauthorized') || urlWithoutQuery.endsWith('/not-found');
  }

  private getParam(route: ActivatedRouteSnapshot, param: string): string | null {
    let snapshot: ActivatedRouteSnapshot | null = route;
    while (snapshot) {
      const value = snapshot.paramMap.get(param);
      if (value) {
        return value;
      }
      snapshot = snapshot.parent;
    }
    return null;
  }

  private async waitForInitialData(skipUsersList = false): Promise<void> {
    // Quick check - if data is already loaded, return immediately
    // For sectionList: need at least one section (hasItems)
    // For usersList: any array is valid including empty (isLoaded) - empty means no users or CLOSE contract
    if (this.hasItems(this.databaseCache.getSectionList?.())) {
      if (skipUsersList || this.isLoaded(this.databaseCache.getUsersList?.())) {
        return;
      }
    }

    // Data not ready - wait with polling
    await this.waitUntil(() => this.hasItems(this.databaseCache.getSectionList?.()))
      .catch(() => console.warn('[UserContextResolver] Section list not ready.'));

    // Skip waiting for users list on error pages (unauthorized, not-found)
    // where users list may be empty due to CLOSE contract
    if (skipUsersList) {
      return;
    }

    // Wait for usersList to be loaded (any array, including empty)
    // This aligns with HomeNavViewModel's loading state check (usersList !== null)
    await this.waitUntil(() => this.isLoaded(this.databaseCache.getUsersList?.()))
      .catch(() => console.warn('[UserContextResolver] Users list not ready.'));
  }

  private hasItems(collection: any): boolean {
    return Array.isArray(collection) && collection.length > 0;
  }

  /**
   * Check if collection is loaded (any array, including empty).
   * This aligns with HomeNavViewModel's loading state check where null = loading, array = loaded.
   */
  private isLoaded(collection: any): boolean {
    return Array.isArray(collection);
  }

  private async waitUntil(condition: () => boolean, timeoutMs = 5000, stepMs = 100): Promise<void> {
    const start = Date.now();
    while (!condition()) {
      if (Date.now() - start > timeoutMs) {
        throw new Error('Timeout while waiting for initial data');
      }
      await this.delay(stepMs);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
